/**
 * FormSchema Composition Hook
 *
 * Computes the diff between a base FormSchema and its tenant override.
 * Accepts pre-fetched data (from portal hooks) so this library package
 * doesn't need direct Apollo access.
 *
 * Used by FormSchemaOverrideDiffPanel for the "Override Inspector" tab.
 */

import { useMemo } from 'react';
import { buildRecursiveDiff, countDiffChanges } from '../utils/diff-utils';
import type { DiffNode } from '../utils/diff-utils';

// =============================================================================
// TYPES
// =============================================================================

/** Minimal field shape — accepts both generated enums and plain strings */
interface FormFieldLike {
  name: string;
  fieldType: string | { toString(): string };
  label?: string | null;
  required?: boolean | null;
  piiClassification?: string | null;
  auditRequired?: boolean | null;
  minimumTier?: string | null;
}

/** Minimal FormSchema shape — avoids importing portal-specific generated types */
interface FormSchemaLike {
  entityType: string;
  subgraph: string;
  version: string;
  displayName: string;
  safetyLevel: string | { toString(): string };
  fields: FormFieldLike[];
  [key: string]: unknown;
}

/** Minimal FormSchemaOverride shape */
interface FormSchemaOverrideLike {
  hiddenFields?: string[] | null;
  readonlyFields?: string[] | null;
  labelOverrides?: string | null;
  customFields?: FormFieldLike[] | null;
  customValidations?: unknown[] | null;
  [key: string]: unknown;
}

export interface FormSchemaCompositionResult {
  /** The merged schema (base + override applied) */
  mergedSchema: FormSchemaLike | null;
  /** The raw override data */
  override: FormSchemaOverrideLike | null;
  /** Whether any override exists */
  hasOverride: boolean;
  /** Recursive diff of field arrays (base vs merged) */
  fieldDiff: DiffNode | null;
  /** Summary counts */
  hiddenCount: number;
  readonlyCount: number;
  labelOverrideCount: number;
  customFieldCount: number;
  /** Total number of changes across all categories */
  totalChanges: number;
  /** Fields affected by the override, with their change type */
  affectedFields: AffectedField[];
  /** Fields with safety implications (CRITICAL/LIFE_CRITICAL hidden or made readonly) */
  safetyWarnings: SafetyWarning[];
}

export interface AffectedField {
  name: string;
  fieldType: string;
  changeType: 'hidden' | 'readonly' | 'label_override' | 'custom_added';
  originalLabel?: string | null;
  newLabel?: string;
}

export interface SafetyWarning {
  fieldName: string;
  safetyLevel: string;
  changeType: 'hidden' | 'readonly';
  message: string;
}

// =============================================================================
// HOOK
// =============================================================================

export function useFormSchemaComposition(
  schema: FormSchemaLike | null,
  override: FormSchemaOverrideLike | null,
): FormSchemaCompositionResult {
  return useMemo(() => {
    if (!schema) {
      return {
        mergedSchema: null,
        override: null,
        hasOverride: false,
        fieldDiff: null,
        hiddenCount: 0,
        readonlyCount: 0,
        labelOverrideCount: 0,
        customFieldCount: 0,
        totalChanges: 0,
        affectedFields: [],
        safetyWarnings: [],
      };
    }

    const hasOverride = !!(
      override &&
      (
        (override.hiddenFields && override.hiddenFields.length > 0) ||
        (override.readonlyFields && override.readonlyFields.length > 0) ||
        (override.labelOverrides && override.labelOverrides !== '{}') ||
        (override.customFields && override.customFields.length > 0) ||
        (override.customValidations && override.customValidations.length > 0)
      )
    );

    // Parse label overrides
    let labelOverrides: Record<string, string> = {};
    if (override?.labelOverrides) {
      try {
        labelOverrides = JSON.parse(override.labelOverrides);
      } catch {
        // Invalid JSON, ignore
      }
    }

    const hiddenFields = new Set(override?.hiddenFields ?? []);
    const readonlyFields = new Set(override?.readonlyFields ?? []);
    const customFields = override?.customFields ?? [];

    // Build affected fields list
    const affectedFields: AffectedField[] = [];
    const safetyWarnings: SafetyWarning[] = [];

    const safetyStr = String(schema.safetyLevel);

    for (const field of schema.fields) {
      const ft = String(field.fieldType);

      if (hiddenFields.has(field.name)) {
        affectedFields.push({
          name: field.name,
          fieldType: ft,
          changeType: 'hidden',
          originalLabel: field.label,
        });
        if (field.piiClassification || field.auditRequired ||
            safetyStr === 'CRITICAL' || safetyStr === 'LIFE_CRITICAL') {
          safetyWarnings.push({
            fieldName: field.name,
            safetyLevel: safetyStr,
            changeType: 'hidden',
            message: `Field "${field.label || field.name}" is hidden but the form has ${safetyStr} safety level${field.piiClassification ? ` and PII classification: ${field.piiClassification}` : ''}`,
          });
        }
      }

      if (readonlyFields.has(field.name)) {
        affectedFields.push({
          name: field.name,
          fieldType: ft,
          changeType: 'readonly',
          originalLabel: field.label,
        });
        if (field.auditRequired ||
            safetyStr === 'CRITICAL' || safetyStr === 'LIFE_CRITICAL') {
          safetyWarnings.push({
            fieldName: field.name,
            safetyLevel: safetyStr,
            changeType: 'readonly',
            message: `Field "${field.label || field.name}" is readonly but the form has ${safetyStr} safety level`,
          });
        }
      }

      if (labelOverrides[field.name]) {
        affectedFields.push({
          name: field.name,
          fieldType: ft,
          changeType: 'label_override',
          originalLabel: field.label,
          newLabel: labelOverrides[field.name],
        });
      }
    }

    for (const cf of customFields) {
      affectedFields.push({
        name: cf.name,
        fieldType: String(cf.fieldType),
        changeType: 'custom_added',
      });
    }

    // Build field-level diff for the tree viewer
    const baseFieldMap: Record<string, unknown> = {};
    for (const f of schema.fields) {
      baseFieldMap[f.name] = {
        type: f.fieldType,
        label: f.label,
        required: f.required,
        visible: true,
        readonly: false,
      };
    }

    const mergedFieldMap: Record<string, unknown> = {};
    for (const f of schema.fields) {
      if (hiddenFields.has(f.name)) continue; // hidden = removed from merged
      mergedFieldMap[f.name] = {
        type: f.fieldType,
        label: labelOverrides[f.name] || f.label,
        required: f.required,
        visible: true,
        readonly: readonlyFields.has(f.name),
      };
    }
    for (const cf of customFields) {
      mergedFieldMap[cf.name] = {
        type: cf.fieldType,
        label: cf.label,
        required: cf.required,
        visible: true,
        readonly: false,
        custom: true,
      };
    }

    const fieldDiff = buildRecursiveDiff(baseFieldMap, mergedFieldMap);
    const diffCounts = countDiffChanges(fieldDiff);

    return {
      mergedSchema: schema,
      override: override ?? null,
      hasOverride,
      fieldDiff,
      hiddenCount: hiddenFields.size,
      readonlyCount: readonlyFields.size,
      labelOverrideCount: Object.keys(labelOverrides).length,
      customFieldCount: customFields.length,
      totalChanges: diffCounts.added + diffCounts.removed + diffCounts.changed,
      affectedFields,
      safetyWarnings,
    };
  }, [schema, override]);
}
